import {
  RECEIVE_DATA,
  MOUSE_DOWN,
  MOUSE_UP,
  MODE_MOVE,
  MODE_EDIT,
  MODE_NONE,
  MOUSE_MOVE,
  MOUSE_LEAVE,
  SAVING,
  STATUS_SAVING,
  SAVED,
  STATUS_SAVED,
  STATUS_DELETING,
  DELETING,
  DELETED,
  CHANGE_COLOR,
  CHANGE_DATE,
  CHANGE_INFO,
  STATUS_UNSAVED,
  DELETE_ITEM,
  STATUS_DELETED,
  MODE_RESIZE_BOTTOM,
  MODE_RESIZE_TOP,
  RESIZE_ROW,
  SET_RANGE,
  DESELECT,
  STATUS_CREATING,
} from "./actions";
import { MIN_BLOCK, resolveItems } from "./utils";
import { mouseDownReducer } from "./mouseDownReducer";
import { mouseMoveReducer } from "./mouseMoveReducer";
import clamp from "lodash/clamp";
import { BlockWithStatus, CalendarItemAction, CalendarState } from "./types";

const toUTC = (now: Date): Date => {
  var utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
  return utc;
};

export const mainReducer = (
  state: CalendarState,
  action: CalendarItemAction
): CalendarState => {
  switch (action.type) {
    case SET_RANGE:
      const [min, max, dayCount, startDate, jumpCount] = action.payload;

      return {
        ...state,
        shownRange: [min, max],
        dayCount,
        startDate,
        jumpCount,
      };

    case RECEIVE_DATA:
      const items: BlockWithStatus[] = action.payload
        .filter((x) => x.start + x.length > state.shownRange[0])
        .filter((x) => x.start < state.shownRange[1])
        .map<BlockWithStatus>((x) => ({
          ...x,
          start: Math.max(state.shownRange[0], x.start),
          length: Math.min(
            state.shownRange[1] - Math.max(state.shownRange[0], x.start),
            x.length
          ),
          date: toUTC(new Date(x.date)),
          status: STATUS_SAVED,
        }))
        .filter((x) => x.length > 0)
        .concat(state.items.filter((x) => x.status === STATUS_CREATING));

      return {
        ...state,
        items: items,
        itemCache: items,
      };
    case MOUSE_DOWN:
      return mouseDownReducer(state, action);

    case DESELECT:
      return {
        ...state,
        activeItem: null,
        mode: MODE_NONE,
      };

    case MOUSE_UP:
      if (state.mode === MODE_EDIT) {
        return state;
      }
      const n = Date.now();
      // is a click
      if (n - state.lastDown < 200 && state.mode === MODE_MOVE) {
        return {
          ...state,
          mode: MODE_EDIT,
        };
      } else {
        if (state.activeItem != null) {
          const { brush } = action.payload;
          const item = state.items.find((x) => x.id === state.activeItem);
          return {
            ...state,
            mode:
              item.status === STATUS_CREATING && !brush.isSwatch
                ? MODE_EDIT
                : MODE_NONE,
            activeItem:
              item.status === STATUS_CREATING && !brush.isSwatch
                ? state.activeItem
                : null,
            items: resolveItems(
              state.items,
              state.activeItem,
              item.date,
              item.start,
              item.length,
              true,
              state.algo,
              state.shownRange[1]
            ),
          };
        } else {
          return {
            ...state,
            mode: MODE_NONE,
          };
        }
      }

    case MOUSE_MOVE:
      return mouseMoveReducer(state, action);

    case MOUSE_LEAVE:
      if (state.mode === MODE_RESIZE_BOTTOM || state.mode === MODE_RESIZE_TOP) {
        return {
          ...state,
          activeItem: null,
          mode: MODE_NONE,
        };
      } else {
        return state;
      }

    case CHANGE_INFO:
      return {
        ...state,
        activeItem: null,
        mode: MODE_NONE,
        items: state.items.map((x) => ({
          ...x,
          status: x.id === state.activeItem ? STATUS_UNSAVED : x.status,
          description:
            x.id === state.activeItem
              ? action.payload.description
              : x.description,
          tags: x.id === state.activeItem ? action.payload.tags : x.tags,
          color: x.id === state.activeItem ? action.payload.color : x.color,
        })),
      };

    case SAVING:
      return {
        ...state,
        items: state.items.map((x) => ({
          ...x,
          status: x.id === action.payload.id ? STATUS_SAVING : x.status,
        })),
      };

    case SAVED:
      return {
        ...state,
        items: state.items
          .filter((x) => x.id !== action.payload.id)
          .concat([
            {
              ...action.payload,
              date: toUTC(new Date(action.payload.date)),
              status: STATUS_SAVED,
            },
          ]),
      };

    case DELETING:
      return {
        ...state,
        items: state.items.map((x) => ({
          ...x,
          status: x.id === action.payload.id ? STATUS_DELETING : x.status,
        })),
      };

    case DELETED:
      return {
        ...state,
        items: state.items.filter((x) => x.id !== action.payload.id),
      };

    case CHANGE_COLOR:
      return {
        ...state,
        brush: action.payload,
      };

    case CHANGE_DATE:
      return {
        ...state,
        startDate: action.payload,
      };

    case DELETE_ITEM:
      return {
        ...state,
        activeItem:
          state.activeItem === action.payload.id ? null : state.activeItem,
        mode: MODE_NONE,
        items: state.items.map((x) => ({
          ...x,
          status: x.id === action.payload.id ? STATUS_DELETED : x.status,
        })),
      };

    case RESIZE_ROW:
      if (action.payload == null) {
        return state;
      }

      const size = state.shownRange[1] - state.shownRange[0];
      const rowHeight = Math.floor(
        clamp((action.payload - 180) / (size / MIN_BLOCK), 18, 50)
      );

      return {
        ...state,
        rowHeight,
      };

    default:
      throw new Error("Invalid Action");
  }
};
